import React, { useRef, FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

export type RevealEventType = 'OPENING' | 'OPENED' | 'CLOSING' | 'CLOSED';

export type RevealSpeed = 'very slow' | 'slow' | 'normal' | 'fast';

export type RevelPosition = 'top left' | 'top right' | 'bottom left' | 'bottom right';

export class RevealEvent {
  constructor(
    public type: RevealEventType,
    public curtainElemRef: HTMLDivElement,
    public revealContentElemRef: HTMLDivElement,
  ) {}
}

interface RevealProps {
  children?: ReactNode;
  contentMinWidth?: number;
  onChange?: (event: RevealEvent) => void;
  reveal?: boolean;
  revealContent?: ReactNode;
  revealCurtainContent?: ReactNode;
  revealPosition?: RevelPosition;
  speed?: RevealSpeed;
}

const RevealOverlay = styled.div`
  position: relative;
  pointer-events: all;
  overflow: hidden;
`;

const RevealCurtain = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-radius: 50%;
  overflow: hidden;
`;

const RevealContent = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  min-width: 500px;
`;

export const Reveal: FunctionComponent<RevealProps> = ({
  children,
  contentMinWidth = 500,
  onChange,
  reveal = false,
  revealContent,
  revealCurtainContent,
  revealPosition = 'top left',
  speed = 'normal',
}) => {
  const forward = useRef(false);
  const animating = useRef(reveal);
  const opened = useRef(reveal);
  const revealCurtainRef = useRef<HTMLDivElement>(null);
  const revealOverlayRef = useRef<HTMLDivElement>(null);
  const revealContentRef = useRef<HTMLDivElement>(null);

  const globalID = useRef(-1);
  const sizeRef = useRef(1);

  // animation logic inspired by https://github.com/nosachamos/react-circular-reveal
  let calculateSize: (currentSize: number, isOpening: boolean) => number;
  switch (speed) {
    case 'very slow':
      calculateSize = (size, isOpening) =>
        isOpening ? size + Math.max(size / 15, 1) : size - Math.max(size / 15, 1);
      break;

    case 'slow':
      calculateSize = (size, isOpening) =>
        isOpening ? size + Math.max(size / 6, 2) : size - Math.max(size / 6, 2);
      break;

    case 'normal':
      calculateSize = (size, isOpening) =>
        isOpening ? size + Math.max(size / 3, 5) : size - Math.max(size / 3, 5);
      break;

    case 'fast':
      calculateSize = (size, isOpening) =>
        isOpening ? size + Math.max(size / 3, 20) : size - Math.max(size / 3, 20);
      break;

    default:
      // eslint-disable-next-line no-console
      console.error('Reveal: Invalid value for speed.');
  }

  const animateCurtain = () => {
    if (revealCurtainRef.current && revealOverlayRef.current && revealContentRef.current) {
      const size = sizeRef.current;
      const curtainStyle = revealCurtainRef.current.style;
      const revealCurtainStyle = revealContentRef.current.style;
      const overlayRect = revealOverlayRef.current.getBoundingClientRect();
      const overlayWidth = overlayRect.width;
      const maxCircularSize = Math.max(overlayRect.height, overlayRect.width) * 2.5;

      curtainStyle.width = `${size}px`;
      curtainStyle.height = `${size}px`;

      revealCurtainStyle.minWidth = `${Math.min(overlayWidth, contentMinWidth)}px`;
      revealCurtainStyle.width = `${overlayWidth}px`;

      switch (revealPosition) {
        case 'top right':
          curtainStyle.right = `${0 - size / 2}px`;
          curtainStyle.top = `${0 - size / 2}px`;
          curtainStyle.left = 'auto';
          revealCurtainStyle.right = `${size / 2}px`;
          revealCurtainStyle.top = `${size / 2}px`;
          revealCurtainStyle.left = 'auto';
          break;
        case 'bottom left':
          curtainStyle.left = `${0 - size / 2}px`;
          curtainStyle.bottom = `${0 - size / 2}px`;
          curtainStyle.top = 'auto';
          revealCurtainStyle.left = `${size / 2}px`;
          revealCurtainStyle.bottom = `${size / 2}px`;
          revealCurtainStyle.top = 'auto';
          break;
        case 'bottom right':
          curtainStyle.right = `${0 - size / 2}px`;
          curtainStyle.bottom = `${0 - size / 2}px`;
          curtainStyle.left = 'auto';
          curtainStyle.top = 'auto';
          revealCurtainStyle.right = `${size / 2}px`;
          revealCurtainStyle.bottom = `${size / 2}px`;
          revealCurtainStyle.left = 'auto';
          revealCurtainStyle.top = 'auto';
          break;
        case 'top left':
        default:
          curtainStyle.left = `${0 - size / 2}px`;
          curtainStyle.top = `${0 - size / 2}px`;
          revealCurtainStyle.left = `${size / 2}px`;
          revealCurtainStyle.top = `${size / 2}px`;
          break;
      }

      if ((forward.current && size < maxCircularSize) || (!forward.current && size > 0)) {
        sizeRef.current = calculateSize(size, forward.current);
        globalID.current = requestAnimationFrame(animateCurtain);
      } else {
        animating.current = false;

        if (!forward.current) {
          curtainStyle.width = '0px';
          curtainStyle.height = '0px';
        }

        if (onChange) {
          onChange(
            new RevealEvent(
              forward.current ? 'OPENED' : 'CLOSED',
              revealCurtainRef.current,
              revealContentRef.current,
            ),
          );
        }
      }
    }
  };

  const doReveal = () => {
    animating.current = true;
    opened.current = !opened.current;
    if (onChange) {
      onChange(
        new RevealEvent(
          !forward.current ? 'OPENING' : 'CLOSING',
          revealCurtainRef.current as HTMLDivElement,
          revealContentRef.current as HTMLDivElement,
        ),
      );
    }
    if (globalID.current !== -1) {
      cancelAnimationFrame(globalID.current);
      globalID.current = -1;
    }
    forward.current = !forward.current;
    animateCurtain();
  };

  if (reveal !== opened.current && !animating.current) {
    doReveal();
  }

  return (
    <RevealOverlay ref={revealOverlayRef} className="reveal-overlay" data-testid="reveal-overlay">
      {children}
      <RevealCurtain ref={revealCurtainRef} className="reveal-curtain" data-testid="reveal-curtain">
        <RevealContent
          ref={revealContentRef}
          className="reveal-content"
          data-testid="reveal-content"
        >
          {revealContent}
        </RevealContent>
        {revealCurtainContent}
      </RevealCurtain>
    </RevealOverlay>
  );
};

export default Reveal;
